import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiArrowLeft,
    FiBox,
    FiCalendar,
    FiCheckCircle,
    FiClock,
    FiCopy,
    FiCreditCard,
    FiEdit3,
    FiFileText,
    FiLoader,
    FiMail,
    FiPhone,
    FiPrinter,
    FiRefreshCw,
    FiUser,
    FiXCircle,
    FiAlertCircle
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    cancelSaleInvoice,
    duplicateSaleInvoice,
    fetchSaleInvoiceById,
    fetchSaleInvoiceHistory,
    saleInvoiceHistorySelector,
    saleInvoiceLoadingSelector,
    selectedSaleInvoiceSelector,
} from "../../../../../../store/billing";
import AllocatePaymentDialog from "./components/AllocatePaymentDialog";
import SaleInvoicePaymentDialog from "./SaleInvoicePaymentDialog";

import "./SaleInvoiceViewPage.css";
import useAuth from "../../../../../../hooks/useAuth";

import {
    fetchSaleInvoicePaymentHistory,
    saleInvoicePaymentSummarySelector,
    reverseSaleInvoicePayment,
    saleInvoicePaymentReversalLoadingSelector,
} from "../../../../../../store/billing/saleInvoicePaymentSlice";

import SaleInvoicePaymentsPanel from "./components/SaleInvoicePaymentsPanel";
import SaleInvoiceStockMovements from "./components/SaleInvoiceStockMovements";
import {
    clearSaleInvoiceStockRetryResult,
    fetchSaleInvoiceStockMovements,
    retrySaleInvoiceStockPosting,
    saleInvoiceStockIncludeReversalsSelector,
    saleInvoiceStockRetryErrorSelector,
    saleInvoiceStockRetryLoadingSelector,
} from "../../../../../../store/billing/saleInvoiceStockSlice";
import CustomerLedgerStatement from "./components/CustomerLedgerStatement";
import SaleInvoicePaymentDetailsDialog from "./components/SaleInvoicePaymentDetailsDialog";

import CreditNotesPanel from "../CreditNotes/components/CreditNotesPanel";
import {
    invoiceCreditNotesSelector,
} from "../../../../../../store/billing/creditNotes/creditNoteSlice";
/*
|--------------------------------------------------------------------------
| ROUTES
|--------------------------------------------------------------------------
*/

const SALE_INVOICE_ROUTE =
    "/billing/sale-invoices";

/*
|--------------------------------------------------------------------------
| TABS
|--------------------------------------------------------------------------
*/

const VIEW_TABS = [
    {
        value:
            "INVOICE",

        label:
            "Invoice",
    },
    {
        value:
            "PAYMENTS",

        label:
            "Payments",
    },
    {
        value: "CREDIT_NOTES",
        label: "Credit Notes",
    },
    {
        value:
            "TIMELINE",

        label:
            "Timeline",
    },
    {
        value: "STOCK",
        label: "Stock",
    },
    {
        value: "LEDGER",
        label: "Customer Ledger",
    },
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const parsed =
        Number(value);

    return Number.isFinite(
        parsed
    )
        ? parsed
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
        100
    ) / 100;

const formatCurrency = (
    value,
    currency = "INR"
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                currency || "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                day:
                    "2-digit",

                month:
                    "short",

                year:
                    "numeric",

                hour:
                    "2-digit",

                minute:
                    "2-digit",
            }
            : {
                day:
                    "2-digit",

                month:
                    "short",

                year:
                    "numeric",
            }
    ).format(date);
};

const formatLabel = (
    value
) => {
    if (!value) {
        return "-";
    }

    return String(value)
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );
};

const getValue = (
    object,
    paths,
    fallback = ""
) => {
    for (
        const path of paths
    ) {
        const value =
            String(path)
                .split(".")
                .reduce(
                    (
                        current,
                        key
                    ) =>
                        current?.[
                        key
                        ],
                    object
                );

        if (
            value !==
            undefined &&
            value !==
            null &&
            value !==
            ""
        ) {
            return value;
        }
    }

    return fallback;
};

const getInvoiceId = (
    invoice
) =>
    invoice?._id ||
    invoice?.id ||
    "";

const getInvoiceNumber = (
    invoice
) =>
    getValue(
        invoice,
        [
            "documentNumber",
            "invoiceNumber",
            "invoiceNo",
            "number",
        ],
        "-"
    );

const getDocumentStatus = (
    invoice
) =>
    String(
        getValue(
            invoice,
            [
                "documentStatus",
                "status",
            ],
            "DRAFT"
        )
    ).toUpperCase();

const getPaymentStatus = (
    invoice
) =>
    String(
        getValue(
            invoice,
            [
                "paymentStatus",
                "paymentSummary.paymentStatus",
            ],
            "UNPAID"
        )
    ).toUpperCase();

const getGrandTotal = (
    invoice
) =>
    toNumber(
        getValue(
            invoice,
            [
                "totals.grandTotal",
                "totals.totalAmount",
                "grandTotal",
                "totalAmount",
            ],
            0
        )
    );

const getPaidAmount = (
    invoice
) =>
    toNumber(
        getValue(
            invoice,
            [
                "totals.paidAmount",
                "paymentSummary.paidAmount",
                "paidAmount",
            ],
            0
        )
    );

const getBalanceAmount = (
    invoice
) => {
    const storedBalance =
        getValue(
            invoice,
            [
                "totals.balanceAmount",
                "paymentSummary.balanceAmount",
                "balanceAmount",
            ],
            null
        );

    if (
        storedBalance !==
        null &&
        storedBalance !==
        undefined
    ) {
        return Math.max(
            roundMoney(
                storedBalance
            ),
            0
        );
    }

    return Math.max(
        roundMoney(
            getGrandTotal(
                invoice
            ) -
            getPaidAmount(
                invoice
            )
        ),
        0
    );
};

const getCustomerName = (
    invoice
) =>
    getValue(
        invoice,
        [
            "customerSnapshot.customerName",
            "customerSnapshot.name",
            "customer.customerName",
            "customer.name",
            "customerName",
        ],
        "Walk-in Customer"
    );

const getCustomerMobile = (
    invoice
) =>
    getValue(
        invoice,
        [
            "customerSnapshot.mobile",
            "customer.mobile",
            "customerMobile",
        ],
        "-"
    );

const getCustomerEmail = (
    invoice
) =>
    getValue(
        invoice,
        [
            "customerSnapshot.email",
            "customer.email",
            "customerEmail",
        ],
        "-"
    );

const getAddressText = (
    address
) => {
    if (!address) {
        return "-";
    }

    if (
        typeof address ===
        "string"
    ) {
        return (
            address.trim() ||
            "-"
        );
    }

    return [
        address.addressLine1 ||
            address.line1,
        address.addressLine2 ||
            address.line2,
        address.address,
        address.city,
        address.state,
        address.pincode ||
            address.postalCode,
        address.country,
    ]
        .filter(Boolean)
        .join(", ") || "-";
};

const getHistoryRecords = (
    historyState
) => {
    if (
        Array.isArray(
            historyState
        )
    ) {
        return historyState;
    }

    if (
        Array.isArray(
            historyState
                ?.records
        )
    ) {
        return historyState
            .records;
    }

    if (
        Array.isArray(
            historyState
                ?.history
        )
    ) {
        return historyState
            .history;
    }

    if (
        Array.isArray(
            historyState
                ?.data
        )
    ) {
        return historyState
            .data;
    }

    return [];
};

// const getPaymentRecords = (
//     invoice
// ) => {
//     const possibleLists = [
//         invoice?.payments,
//         invoice?.paymentHistory,
//         invoice?.paymentAllocations,
//         invoice?.allocations,
//     ];

//     return (
//         possibleLists.find(
//             Array.isArray
//         ) || []
//     );
// };

const canReceivePayment = (
    invoice
) => {
    const documentStatus =
        getDocumentStatus(
            invoice
        );

    const paymentStatus =
        getPaymentStatus(
            invoice
        );

    return (
        documentStatus ===
        "CONFIRMED" &&
        [
            "UNPAID",
            "PARTIALLY_PAID",
        ].includes(
            paymentStatus
        ) &&
        getBalanceAmount(
            invoice
        ) > 0
    );
};

const getErrorMessage = (
    error,
    fallback
) =>
    error?.message ||
    error?.payload?.message ||
    error?.payload ||
    error?.response?.data
        ?.message ||
    fallback;

/*
|--------------------------------------------------------------------------
| INFO FIELD
|--------------------------------------------------------------------------
*/

const InfoField = ({
    label,
    value,
}) => (
    <div className="sale-invoice-view-info-field">
        <span>
            {label}
        </span>

        <strong>
            {value || "-"}
        </strong>
    </div>
);

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SaleInvoiceViewPage =
    () => {
        const { user } =
            useAuth();

        const paymentHistorySummary =
            useSelector(
                saleInvoicePaymentSummarySelector
            );
        const dispatch =
            useDispatch();

        const history =
            useHistory();

        const {
            id,
        } =
            useParams();
        const paymentReversalLoading =
            useSelector(
                saleInvoicePaymentReversalLoadingSelector
            );
        const invoice =
            useSelector(
                selectedSaleInvoiceSelector
            );

        const historyState =
            useSelector(
                saleInvoiceHistorySelector
            );

        const loadingState =
            useSelector(
                saleInvoiceLoadingSelector
            ) || {};

        const retryingStock =
            useSelector(
                saleInvoiceStockRetryLoadingSelector
            );

        const retryStockError =
            useSelector(
                saleInvoiceStockRetryErrorSelector
            );

        const includeStockReversals =
            useSelector(
                saleInvoiceStockIncludeReversalsSelector
            );

        const [
            ledgerSelectedPayment,
            setLedgerSelectedPayment,
        ] = useState(null);

        const [
            ledgerPaymentDialogOpen,
            setLedgerPaymentDialogOpen,
        ] = useState(false);

        const invoiceCreditNotes =
            useSelector(
                invoiceCreditNotesSelector
            ) || [];

        const [
            activeTab,
            setActiveTab,
        ] = useState(
            "INVOICE"
        );

        const [
            paymentDialogOpen,
            setPaymentDialogOpen,
        ] = useState(
            false
        );

        const [
            actionLoading,
            setActionLoading,
        ] = useState(
            ""
        );

        const [
            pageError,
            setPageError,
        ] = useState(
            ""
        );

        const [
            allocatePaymentDialogOpen,
            setAllocatePaymentDialogOpen,
        ] = useState(false);

        const [
            paymentForAllocation,
            setPaymentForAllocation,
        ] = useState(null);

        const loading =
            Boolean(
                loadingState
                    .detailsLoading ||
                loadingState
                    .fetchByIdLoading
            );

        /*
        |--------------------------------------------------------------------------
        | LOGGED-IN TENANT BUSINESS DETAILS
        |--------------------------------------------------------------------------
        */

        const tenant =
            user?.tenant || {};

        const tenantAddress =
            tenant?.address || {};

        const tenantBranding =
            tenant?.branding || {};

        const tenantSettings =
            tenant?.settings || {};

        const tenantLegalName =
            String(
                tenant?.legalName ||
                tenant?.name ||
                "BUSINESS"
            )
                .trim()
                .toUpperCase();

        const tenantMobile =
            tenant?.mobile ||
            "-";

        const tenantEmail =
            tenant?.email ||
            "-";

        const tenantGstin =
            tenant?.gstNumber ||
            "-";

        const tenantPan =
            tenant?.panNumber ||
            "-";

        const tenantAddressText =
            getAddressText(
                tenantAddress
            );

        const tenantCurrency =
            String(
                tenantSettings?.currency ||
                "INR"
            )
                .trim()
                .toUpperCase();

        const displayCurrency =
            useCallback(
                (
                    value
                ) =>
                    formatCurrency(
                        value,
                        tenantCurrency
                    ),
                [
                    tenantCurrency,
                ]
            );

        const historyLoading =
            Boolean(
                loadingState
                    .historyLoading
            );

        const invoiceId =
            getInvoiceId(
                invoice
            ) || id;

        const invoiceNumber =
            getInvoiceNumber(
                invoice
            );

        const documentStatus =
            getDocumentStatus(
                invoice
            );





        const paymentStatus =
            getPaymentStatus(
                invoice
            );

        const grandTotal =
            getGrandTotal(
                invoice
            );

        const paidAmount =
            getPaidAmount(
                invoice
            );

        const balanceAmount =
            getBalanceAmount(
                invoice
            );

        const items =
            useMemo(
                () =>
                    Array.isArray(
                        invoice?.items
                    )
                        ? invoice.items
                        : [],
                [invoice]
            );
        const subtotal =
            roundMoney(
                getValue(
                    invoice,
                    [
                        "totals.subtotal",
                        "totals.subTotal",
                        "totals.taxableAmount",
                        "totals.itemGrossAmount",
                        "subtotal",
                        "subTotal",
                        "taxableAmount",
                    ],
                    items.reduce(
                        (
                            total,
                            item
                        ) => {
                            const quantity =
                                toNumber(
                                    item?.quantity ??
                                    item?.qty ??
                                    0
                                );

                            const rate =
                                toNumber(
                                    item?.rate ??
                                    item?.unitPrice ??
                                    item?.price ??
                                    0
                                );

                            const itemDiscount =
                                toNumber(
                                    item?.discountAmount ??
                                    item?.discount ??
                                    0
                                );

                            return (
                                total +
                                quantity * rate -
                                itemDiscount
                            );
                        },
                        0
                    )
                )
            );

        const discountAmount =
            roundMoney(
                getValue(
                    invoice,
                    [
                        "totals.discountAmount",
                        "totals.totalDiscount",
                        "totals.invoiceDiscountAmount",
                        "discountAmount",
                        "totalDiscount",
                        "invoiceDiscountAmount",
                    ],
                    items.reduce(
                        (
                            total,
                            item
                        ) =>
                            total +
                            toNumber(
                                item?.discountAmount ??
                                item?.discount ??
                                0
                            ),
                        0
                    )
                )
            );

        const taxAmount =
            roundMoney(
                getValue(
                    invoice,
                    [
                        "totals.taxAmount",
                        "totals.totalTax",
                        "taxAmount",
                        "totalTax",
                    ],
                    items.reduce(
                        (
                            total,
                            item
                        ) =>
                            total +
                            toNumber(
                                item?.taxAmount ??
                                item?.totalTax ??
                                0
                            ),
                        0
                    )
                )
            );

        const additionalChargesAmount =
            roundMoney(
                getValue(
                    invoice,
                    [
                        "totals.additionalChargesAmount",
                        "totals.additionalCharges",
                        "additionalChargesAmount",
                        "additionalCharges",
                    ],
                    0
                )
            );

        const roundOffAmount =
            roundMoney(
                getValue(
                    invoice,
                    [
                        "totals.roundOffAmount",
                        "totals.roundOff",
                        "roundOffAmount",
                        "roundOff",
                    ],
                    0
                )
            );



        // const payments =
        //     useMemo(
        //         () =>
        //             getPaymentRecords(
        //                 invoice
        //             ),
        //         [invoice]
        //     );

        const timeline =
            useMemo(
                () =>
                    getHistoryRecords(
                        historyState
                    ),
                [historyState]
            );

        const loadInvoice =
            useCallback(
                async () => {
                    if (!id) {
                        return;
                    }

                    try {
                        setPageError(
                            ""
                        );

                        await Promise.all([
                            dispatch(
                                fetchSaleInvoiceById(
                                    id
                                )
                            ).unwrap(),

                            dispatch(
                                fetchSaleInvoiceHistory({
                                    documentId:
                                        id,

                                    invoiceId:
                                        id,

                                    page:
                                        1,

                                    limit:
                                        100,
                                })
                            ).unwrap(),
                        ]);
                    } catch (
                    error
                    ) {
                        setPageError(
                            getErrorMessage(
                                error,
                                "Unable to load sale invoice."
                            )
                        );
                    }
                },
                [
                    dispatch,
                    id,
                ]
            );

        useEffect(() => {
            loadInvoice();
        }, [loadInvoice]);

        const refreshInvoicePaymentData =
            useCallback(
                async () => {
                    if (
                        !invoiceId
                    ) {
                        return;
                    }

                    await Promise.all([
                        dispatch(
                            fetchSaleInvoiceById(
                                invoiceId
                            )
                        ).unwrap(),

                        dispatch(
                            fetchSaleInvoicePaymentHistory({
                                invoiceId,

                                page:
                                    1,

                                limit:
                                    100,

                                includeReversed:
                                    true,
                            })
                        ).unwrap(),

                        dispatch(
                            fetchSaleInvoiceHistory({
                                documentId:
                                    invoiceId,

                                invoiceId,

                                page:
                                    1,

                                limit:
                                    100,
                            })
                        ).unwrap(),
                    ]);
                },
                [
                    dispatch,
                    invoiceId,
                ]
            );

        const handlePaymentSuccess =
            useCallback(
                async () => {
                    try {
                        setPageError(
                            ""
                        );

                        setPaymentDialogOpen(
                            false
                        );

                        await refreshInvoicePaymentData();

                        setActiveTab(
                            "PAYMENTS"
                        );
                    } catch (
                    error
                    ) {
                        setPageError(
                            getErrorMessage(
                                error,
                                "Payment was recorded, but the invoice data could not be refreshed."
                            )
                        );
                    }
                },
                [
                    refreshInvoicePaymentData,
                ]
            );

        const handleDuplicate =
            async () => {
                if (!invoiceId) {
                    return;
                }

                try {
                    setActionLoading(
                        "DUPLICATE"
                    );

                    setPageError(
                        ""
                    );

                    const result =
                        await dispatch(
                            duplicateSaleInvoice({
                                documentId:
                                    invoiceId,

                                payload:
                                    {},
                            })
                        ).unwrap();

                    const duplicatedInvoice =
                        result?.invoice ||
                        result?.data
                            ?.invoice ||
                        result?.data ||
                        result;

                    const duplicatedId =
                        getInvoiceId(
                            duplicatedInvoice
                        );

                    if (
                        duplicatedId
                    ) {
                        history.push(
                            `${SALE_INVOICE_ROUTE}/${duplicatedId}/edit`
                        );

                        return;
                    }

                    history.push(
                        SALE_INVOICE_ROUTE
                    );
                } catch (
                error
                ) {
                    setPageError(
                        getErrorMessage(
                            error,
                            "Unable to duplicate sale invoice."
                        )
                    );
                } finally {
                    setActionLoading(
                        ""
                    );
                }
            };

        const handleCancel =
            async () => {
                if (
                    !invoiceId ||
                    documentStatus !==
                    "CONFIRMED"
                ) {
                    return;
                }

                const reason =
                    window.prompt(
                        `Enter cancellation reason for ${invoiceNumber}:`
                    );

                if (
                    !reason?.trim()
                ) {
                    return;
                }

                try {
                    setActionLoading(
                        "CANCEL"
                    );

                    setPageError(
                        ""
                    );

                    /*
                    |--------------------------------------------------------------------------
                    | CANCEL SALE INVOICE
                    |--------------------------------------------------------------------------
                    |
                    | createDocumentSlice.cancelDocument expects `reason` as a
                    | TOP-LEVEL argument. It validates reason before calling the
                    | backend. Previously reason existed only inside `payload`,
                    | so the thunk rejected locally and the API was never called.
                    |
                    |--------------------------------------------------------------------------
                    */

                    const cancellationReason =
                        reason.trim();

                    await dispatch(
                        cancelSaleInvoice({
                            documentId:
                                invoiceId,

                            reason:
                                cancellationReason,

                            payload: {
                                cancellationReason,
                            },
                        })
                    ).unwrap();

                    await Promise.all([
                        loadInvoice(),

                        dispatch(
                            fetchSaleInvoicePaymentHistory({
                                invoiceId,

                                page:
                                    1,

                                limit:
                                    100,

                                includeReversed:
                                    true,
                            })
                        ).unwrap(),

                        dispatch(
                            fetchSaleInvoiceStockMovements({
                                invoiceId,

                                includeReversals:
                                    true,
                            })
                        ).unwrap(),
                    ]);
                } catch (
                error
                ) {
                    setPageError(
                        getErrorMessage(
                            error,
                            "Unable to cancel sale invoice."
                        )
                    );
                } finally {
                    setActionLoading(
                        ""
                    );
                }
            };

        const getInventoryPostingStatus = (
            invoice
        ) =>
            String(
                invoice
                    ?.inventoryPostingStatus ||
                "PENDING"
            )
                .trim()
                .toUpperCase();

        const getInventoryPostingError = (
            invoice
        ) =>
            String(
                invoice
                    ?.inventoryPostingError ||
                ""
            ).trim();

        const getInventoryStatusLabel = (
            status
        ) => {
            const normalizedStatus =
                String(
                    status ||
                    "PENDING"
                ).toUpperCase();

            const labels = {
                PENDING:
                    "Stock Pending",

                POSTED:
                    "Stock Posted",

                REVERSED:
                    "Stock Reversed",

                FAILED:
                    "Stock Posting Failed",

                NOT_APPLICABLE:
                    "Stock Not Applicable",
            };

            return (
                labels[
                normalizedStatus
                ] ||
                formatLabel(
                    normalizedStatus
                )
            );
        };

        const handleRetryStockPosting =
            useCallback(
                async () => {
                    if (
                        !invoiceId ||
                        retryingStock
                    ) {
                        return;
                    }

                    try {
                        setPageError(
                            ""
                        );

                        dispatch(
                            clearSaleInvoiceStockRetryResult()
                        );

                        await dispatch(
                            retrySaleInvoiceStockPosting({
                                invoiceId,
                            })
                        ).unwrap();

                        await Promise.all([
                            dispatch(
                                fetchSaleInvoiceById(
                                    invoiceId
                                )
                            ).unwrap(),

                            dispatch(
                                fetchSaleInvoiceStockMovements({
                                    invoiceId,

                                    includeReversals:
                                        includeStockReversals,
                                })
                            ).unwrap(),
                        ]);

                        setActiveTab(
                            "STOCK"
                        );
                    } catch (
                    error
                    ) {
                        setPageError(
                            error?.message ||
                            error ||
                            "Unable to retry stock posting."
                        );
                    }
                },
                [
                    dispatch,
                    includeStockReversals,
                    invoiceId,
                    retryingStock,
                ]
            );

        const inventoryPostingStatus =
            getInventoryPostingStatus(
                invoice
            );

        const inventoryPostingError =
            getInventoryPostingError(
                invoice
            );

        const mapLedgerEntryToPayment =
            useCallback(
                (
                    entry
                ) => {
                    if (
                        !entry
                    ) {
                        return null;
                    }

                    const payment =
                        entry?.payment &&
                            typeof entry.payment ===
                            "object"
                            ? entry.payment
                            : {};

                    const allocation =
                        entry
                            ?.paymentAllocation &&
                            typeof entry
                                .paymentAllocation ===
                            "object"
                            ? entry
                                .paymentAllocation
                            : {};

                    const paymentId =
                        payment?._id ||
                        entry?.paymentId ||
                        allocation
                            ?.payment
                            ?._id ||
                        allocation
                            ?.payment ||
                        null;

                    const allocationId =
                        allocation?._id ||
                        (
                            typeof entry
                                ?.paymentAllocation ===
                                "string"
                                ? entry
                                    .paymentAllocation
                                : null
                        ) ||
                        entry
                            ?.allocationId ||
                        null;

                    const customerValue =
                        payment
                            ?.customer ||
                        entry
                            ?.customer ||
                        allocation
                            ?.customer ||
                        invoice
                            ?.customer ||
                        null;

                    const customerId =
                        payment
                            ?.customerId ||
                        payment
                            ?.customer
                            ?._id ||
                        (
                            typeof payment
                                ?.customer ===
                                "string"
                                ? payment
                                    .customer
                                : null
                        ) ||
                        entry
                            ?.customerId ||
                        entry
                            ?.customer
                            ?._id ||
                        (
                            typeof entry
                                ?.customer ===
                                "string"
                                ? entry
                                    .customer
                                : null
                        ) ||
                        allocation
                            ?.customer
                            ?._id ||
                        (
                            typeof allocation
                                ?.customer ===
                                "string"
                                ? allocation
                                    .customer
                                : null
                        ) ||
                        invoice
                            ?.customer
                            ?._id ||
                        (
                            typeof invoice
                                ?.customer ===
                                "string"
                                ? invoice
                                    .customer
                                : null
                        ) ||
                        invoice
                            ?.customerId ||
                        invoice
                            ?.customerSnapshot
                            ?.customerId ||
                        invoice
                            ?.customerSnapshot
                            ?._id ||
                        null;

                    const document =
                        entry?.document &&
                            typeof entry.document ===
                            "object"
                            ? entry.document
                            : {
                                _id:
                                    entry
                                        ?.sourceId ||
                                    invoiceId,

                                documentNumber:
                                    invoiceNumber,

                                documentDate:
                                    invoice
                                        ?.documentDate ||
                                    null,
                            };

                    const paymentAmount =
                        roundMoney(
                            payment
                                ?.amount ??
                            entry
                                ?.paymentAmount ??
                            entry
                                ?.amount ??
                            entry
                                ?.credit ??
                            allocation
                                ?.allocatedAmount ??
                            allocation
                                ?.activeAmount ??
                            0
                        );

                    const allocatedAmount =
                        roundMoney(
                            allocation
                                ?.allocatedAmount ??
                            entry
                                ?.allocatedAmount ??
                            entry
                                ?.activeAmount ??
                            entry
                                ?.amount ??
                            entry
                                ?.credit ??
                            0
                        );

                    const reversedAmount =
                        roundMoney(
                            allocation
                                ?.reversedAmount ??
                            entry
                                ?.reversedAmount ??
                            (
                                entry
                                    ?.isReversal
                                    ? entry
                                        ?.amount
                                    : 0
                            ) ??
                            0
                        );

                    const isReversed =
                        String(
                            allocation
                                ?.status ||
                            entry
                                ?.status ||
                            payment
                                ?.status ||
                            ""
                        ).toUpperCase() ===
                        "REVERSED" ||
                        Boolean(
                            entry
                                ?.isReversal ||
                            payment
                                ?.isReversed ||
                            payment
                                ?.reversal
                                ?.isReversed
                        );

                    const activeAmount =
                        isReversed
                            ? 0
                            : roundMoney(
                                allocation
                                    ?.activeAmount ??
                                entry
                                    ?.activeAmount ??
                                Math.max(
                                    allocatedAmount -
                                    reversedAmount,
                                    0
                                )
                            );

                    const explicitAvailable =
                        payment
                            ?.unallocatedAmount ??
                        entry
                            ?.unallocatedAmount ??
                        entry
                            ?.availableAmount;

                    const unallocatedAmount =
                        isReversed
                            ? 0
                            : roundMoney(
                                explicitAvailable !==
                                    undefined &&
                                    explicitAvailable !==
                                    null
                                    ? explicitAvailable
                                    : Math.max(
                                        paymentAmount -
                                        allocatedAmount,
                                        0
                                    )
                            );

                    const paymentStatus =
                        isReversed
                            ? "REVERSED"
                            : String(
                                payment
                                    ?.paymentStatus ||
                                payment
                                    ?.status ||
                                entry
                                    ?.paymentStatus ||
                                (
                                    unallocatedAmount <=
                                        0 &&
                                        allocatedAmount >
                                        0
                                        ? "FULLY_ALLOCATED"
                                        : allocatedAmount >
                                            0
                                            ? "PARTIALLY_ALLOCATED"
                                            : "COMPLETED"
                                )
                            ).toUpperCase();

                    const allocationStatus =
                        isReversed
                            ? "REVERSED"
                            : String(
                                allocation
                                    ?.status ||
                                entry
                                    ?.allocationStatus ||
                                entry
                                    ?.status ||
                                "ACTIVE"
                            ).toUpperCase();

                    const allocationRecord =
                        allocatedAmount >
                            0
                            ? {
                                _id:
                                    allocationId,

                                payment:
                                    paymentId,

                                document,

                                documentNumber:
                                    document
                                        ?.documentNumber ||
                                    invoiceNumber,

                                allocationDate:
                                    allocation
                                        ?.allocationDate ||
                                    entry
                                        ?.transactionDate ||
                                    entry
                                        ?.postingDate ||
                                    entry
                                        ?.createdAt ||
                                    null,

                                allocatedAmount,

                                reversedAmount,

                                activeAmount,

                                status:
                                    allocationStatus,

                                customer:
                                    customerId,
                            }
                            : null;

                    return {
                        ...payment,

                        _id:
                            paymentId,

                        paymentId,

                        allocationId,

                        receiptNumber:
                            payment
                                ?.receiptNumber ||
                            allocation
                                ?.paymentReceiptNumber ||
                            entry
                                ?.receiptNumber ||
                            entry
                                ?.referenceNumber ||
                            "-",

                        paymentDate:
                            payment
                                ?.paymentDate ||
                            allocation
                                ?.allocationDate ||
                            entry
                                ?.transactionDate ||
                            entry
                                ?.postingDate ||
                            entry
                                ?.createdAt ||
                            null,

                        paymentMethod:
                            payment
                                ?.paymentMethod ||
                            entry
                                ?.paymentMethod ||
                            "",

                        status:
                            paymentStatus,

                        paymentStatus,

                        allocationStatus,

                        amount:
                            paymentAmount,

                        paymentAmount,

                        allocatedAmount,

                        unallocatedAmount,

                        availableAmount:
                            unallocatedAmount,

                        activeAmount,

                        reversedAmount,

                        referenceNumber:
                            payment
                                ?.referenceNumber ||
                            entry
                                ?.referenceNumber ||
                            "",

                        transactionId:
                            payment
                                ?.transactionId ||
                            entry
                                ?.transactionId ||
                            "",

                        bankReferenceNumber:
                            payment
                                ?.bankReferenceNumber ||
                            entry
                                ?.bankReferenceNumber ||
                            "",

                        upiTransactionId:
                            payment
                                ?.upiTransactionId ||
                            entry
                                ?.upiTransactionId ||
                            "",

                        cardTransactionId:
                            payment
                                ?.cardTransactionId ||
                            entry
                                ?.cardTransactionId ||
                            "",

                        chequeNumber:
                            payment
                                ?.chequeNumber ||
                            entry
                                ?.chequeNumber ||
                            "",

                        isReversed,

                        reversedAt:
                            payment
                                ?.reversal
                                ?.reversedAt ||
                            payment
                                ?.reversedAt ||
                            entry
                                ?.reversedAt ||
                            null,

                        reversalReason:
                            payment
                                ?.reversal
                                ?.reason ||
                            payment
                                ?.reversalReason ||
                            entry
                                ?.reversalReason ||
                            "",

                        notes:
                            payment
                                ?.notes ||
                            entry
                                ?.internalNotes ||
                            entry
                                ?.narration ||
                            "",

                        customer:
                            customerValue ||
                            customerId,

                        customerId,

                        websiteUser:
                            payment
                                ?.websiteUser ||
                            entry
                                ?.websiteUser ||
                            invoice
                                ?.websiteUser ||
                            null,

                        customerSnapshot: {
                            ...(
                                invoice
                                    ?.customerSnapshot ||
                                {}
                            ),

                            ...(
                                entry
                                    ?.customerSnapshot ||
                                {}
                            ),

                            ...(
                                payment
                                    ?.customerSnapshot ||
                                {}
                            ),

                            customerName:
                                payment
                                    ?.customerSnapshot
                                    ?.customerName ||
                                entry
                                    ?.customer
                                    ?.customerName ||
                                entry
                                    ?.customer
                                    ?.name ||
                                invoice
                                    ?.customerSnapshot
                                    ?.customerName ||
                                invoice
                                    ?.customer
                                    ?.name ||
                                "",

                            mobile:
                                payment
                                    ?.customerSnapshot
                                    ?.mobile ||
                                entry
                                    ?.customer
                                    ?.mobile ||
                                invoice
                                    ?.customerSnapshot
                                    ?.mobile ||
                                invoice
                                    ?.customer
                                    ?.mobile ||
                                "",
                        },

                        customerName:
                            payment
                                ?.customerName ||
                            entry
                                ?.customer
                                ?.customerName ||
                            entry
                                ?.customer
                                ?.name ||
                            getCustomerName(
                                invoice
                            ),

                        customerMobile:
                            payment
                                ?.customerMobile ||
                            entry
                                ?.customer
                                ?.mobile ||
                            getCustomerMobile(
                                invoice
                            ),

                        document,

                        documentId:
                            document
                                ?._id ||
                            invoiceId,

                        documentNumber:
                            document
                                ?.documentNumber ||
                            invoiceNumber,

                        allocations:
                            allocationRecord
                                ? [
                                    allocationRecord,
                                ]
                                : [],

                        createdAt:
                            entry
                                ?.createdAt ||
                            payment
                                ?.createdAt ||
                            null,

                        updatedAt:
                            entry
                                ?.updatedAt ||
                            payment
                                ?.updatedAt ||
                            null,
                    };
                },
                [
                    invoice,
                    invoiceId,
                    invoiceNumber,
                ]
            );
        const handleOpenPaymentDetails =
            useCallback(
                (
                    paymentOrLedgerEntry
                ) => {
                    if (
                        !paymentOrLedgerEntry
                    ) {
                        return;
                    }

                    const isLedgerEntry =
                        Boolean(
                            paymentOrLedgerEntry
                                ?.transactionType ||
                            paymentOrLedgerEntry
                                ?.direction ||
                            paymentOrLedgerEntry
                                ?.paymentAllocation
                        );

                    const mappedPayment =
                        isLedgerEntry
                            ? mapLedgerEntryToPayment(
                                paymentOrLedgerEntry
                            )
                            : paymentOrLedgerEntry;

                    const paymentId =
                        mappedPayment
                            ?._id ||
                        mappedPayment
                            ?.paymentId ||
                        mappedPayment
                            ?.payment
                            ?._id ||
                        null;

                    if (
                        !paymentId
                    ) {
                        setPageError(
                            "Payment details are not available for this record."
                        );

                        return;
                    }

                    const customerId =
                        mappedPayment
                            ?.customerId ||
                        mappedPayment
                            ?.customer
                            ?._id ||
                        (
                            typeof mappedPayment
                                ?.customer ===
                                "string"
                                ? mappedPayment
                                    .customer
                                : null
                        ) ||
                        invoice
                            ?.customer
                            ?._id ||
                        (
                            typeof invoice
                                ?.customer ===
                                "string"
                                ? invoice
                                    .customer
                                : null
                        ) ||
                        invoice
                            ?.customerId ||
                        invoice
                            ?.customerSnapshot
                            ?.customerId ||
                        invoice
                            ?.customerSnapshot
                            ?._id ||
                        null;

                    const paymentAmount =
                        roundMoney(
                            mappedPayment
                                ?.amount ??
                            mappedPayment
                                ?.paymentAmount ??
                            mappedPayment
                                ?.payment
                                ?.amount ??
                            mappedPayment
                                ?.allocatedAmount ??
                            mappedPayment
                                ?.activeAmount ??
                            0
                        );

                    const allocatedAmount =
                        roundMoney(
                            mappedPayment
                                ?.allocatedAmount ??
                            mappedPayment
                                ?.payment
                                ?.allocatedAmount ??
                            mappedPayment
                                ?.activeAmount ??
                            0
                        );

                    const explicitAvailable =
                        mappedPayment
                            ?.unallocatedAmount ??
                        mappedPayment
                            ?.availableAmount ??
                        mappedPayment
                            ?.payment
                            ?.unallocatedAmount;

                    const unallocatedAmount =
                        roundMoney(
                            explicitAvailable !==
                                undefined &&
                                explicitAvailable !==
                                null
                                ? explicitAvailable
                                : Math.max(
                                    paymentAmount -
                                    allocatedAmount,
                                    0
                                )
                        );

                    setPageError(
                        ""
                    );

                    setLedgerSelectedPayment({
                        ...mappedPayment,

                        _id:
                            paymentId,

                        paymentId,

                        amount:
                            paymentAmount,

                        paymentAmount,

                        allocatedAmount,

                        unallocatedAmount,

                        availableAmount:
                            unallocatedAmount,

                        customer:
                            mappedPayment
                                ?.customer ||
                            customerId,

                        customerId,

                        document:
                            mappedPayment
                                ?.document ||
                            {
                                _id:
                                    invoiceId,

                                documentNumber:
                                    invoiceNumber,

                                documentDate:
                                    invoice
                                        ?.documentDate ||
                                    null,
                            },

                        documentId:
                            mappedPayment
                                ?.documentId ||
                            mappedPayment
                                ?.document
                                ?._id ||
                            invoiceId,

                        documentNumber:
                            mappedPayment
                                ?.documentNumber ||
                            mappedPayment
                                ?.document
                                ?.documentNumber ||
                            invoiceNumber,

                        customerSnapshot: {
                            ...(
                                invoice
                                    ?.customerSnapshot ||
                                {}
                            ),

                            ...(
                                mappedPayment
                                    ?.customerSnapshot ||
                                {}
                            ),
                        },
                    });

                    setLedgerPaymentDialogOpen(
                        true
                    );
                },
                [
                    invoice,
                    invoiceId,
                    invoiceNumber,
                    mapLedgerEntryToPayment,
                ]
            );
        const handleOpenAllocatePayment =
            useCallback(
                (
                    payment
                ) => {
                    const paymentId =
                        payment?._id ||
                        payment?.paymentId ||
                        payment?.payment?._id ||
                        null;

                    const availableAmount =
                        roundMoney(
                            payment
                                ?.unallocatedAmount ??
                            payment
                                ?.availableAmount ??
                            payment
                                ?.payment
                                ?.unallocatedAmount ??
                            Math.max(
                                toNumber(
                                    payment
                                        ?.amount ??
                                    payment
                                        ?.paymentAmount ??
                                    payment
                                        ?.payment
                                        ?.amount
                                ) -
                                toNumber(
                                    payment
                                        ?.allocatedAmount ??
                                    payment
                                        ?.payment
                                        ?.allocatedAmount
                                ),
                                0
                            )
                        );

                    if (
                        !paymentId
                    ) {
                        setPageError(
                            "Payment ID is missing."
                        );

                        return;
                    }

                    if (
                        availableAmount <=
                        0
                    ) {
                        setPageError(
                            "This payment is fully allocated. No amount is available for further allocation."
                        );

                        return;
                    }

                    setPageError(
                        ""
                    );

                    setPaymentForAllocation({
                        ...payment,

                        _id:
                            paymentId,

                        paymentId,

                        unallocatedAmount:
                            availableAmount,

                        availableAmount,

                        customer:
                            payment
                                ?.customer ||
                            payment
                                ?.customerId ||
                            invoice
                                ?.customer ||
                            null,

                        customerId:
                            payment
                                ?.customerId ||
                            payment
                                ?.customer
                                ?._id ||
                            (
                                typeof payment
                                    ?.customer ===
                                    "string"
                                    ? payment
                                        .customer
                                    : null
                            ) ||
                            invoice
                                ?.customer
                                ?._id ||
                            (
                                typeof invoice
                                    ?.customer ===
                                    "string"
                                    ? invoice
                                        .customer
                                    : null
                            ) ||
                            invoice
                                ?.customerId ||
                            invoice
                                ?.customerSnapshot
                                ?.customerId ||
                            invoice
                                ?.customerSnapshot
                                ?._id ||
                            null,

                        websiteUser:
                            payment
                                ?.websiteUser ||
                            invoice
                                ?.websiteUser ||
                            null,

                        customerSnapshot: {
                            ...(
                                invoice
                                    ?.customerSnapshot ||
                                {}
                            ),

                            ...(
                                payment
                                    ?.customerSnapshot ||
                                {}
                            ),
                        },
                    });

                    setAllocatePaymentDialogOpen(
                        true
                    );
                },
                [
                    invoice,
                ]
            );

        const handleCloseAllocatePayment =
            () => {
                setAllocatePaymentDialogOpen(
                    false
                );

                setPaymentForAllocation(
                    null
                );
            };

        const handleReversePayment =
            useCallback(
                async (
                    payment,
                    reason
                ) => {
                    const paymentId =
                        payment?._id ||
                        payment?.paymentId ||
                        payment?.payment?._id ||
                        null;

                    const normalizedReason =
                        String(
                            reason ||
                            ""
                        ).trim();

                    if (
                        !paymentId
                    ) {
                        const error =
                            new Error(
                                "Payment ID is missing."
                            );

                        setPageError(
                            error.message
                        );

                        throw error;
                    }

                    if (
                        normalizedReason.length <
                        3
                    ) {
                        const error =
                            new Error(
                                "Please enter a valid reversal reason."
                            );

                        setPageError(
                            error.message
                        );

                        throw error;
                    }

                    try {
                        setPageError(
                            ""
                        );

                        await dispatch(
                            reverseSaleInvoicePayment({
                                paymentId,

                                reason:
                                    normalizedReason,
                            })
                        ).unwrap();

                        setLedgerPaymentDialogOpen(
                            false
                        );

                        setLedgerSelectedPayment(
                            null
                        );

                        await refreshInvoicePaymentData();

                        setActiveTab(
                            "PAYMENTS"
                        );

                        return true;
                    } catch (
                    error
                    ) {
                        const message =
                            getErrorMessage(
                                error,
                                "Unable to reverse payment."
                            );

                        setPageError(
                            message
                        );

                        throw new Error(
                            message
                        );
                    }
                },
                [
                    dispatch,
                    refreshInvoicePaymentData,
                ]
            );

        if (loading) {
            return (
                <div className="sale-invoice-view-state">
                    <FiLoader className="sale-invoice-view-spinner" />

                    <strong>
                        Loading invoice
                    </strong>

                    <span>
                        Please wait while invoice details are loaded.
                    </span>
                </div>
            );
        }

        if (!invoice) {
            return (
                <div className="sale-invoice-view-state">
                    <FiFileText />

                    <strong>
                        Invoice not found
                    </strong>

                    <span>
                        The requested sale invoice could not be loaded.
                    </span>

                    <button
                        type="button"
                        onClick={() =>
                            history.push(
                                SALE_INVOICE_ROUTE
                            )
                        }
                    >
                        Back to Invoices
                    </button>
                </div>
            );
        }

        return (
            <div className="sale-invoice-view-page">
                <header className="sale-invoice-view-header">
                    <div className="sale-invoice-view-heading">
                        <button
                            type="button"
                            className="sale-invoice-view-back"
                            onClick={() =>
                                history.push(
                                    SALE_INVOICE_ROUTE
                                )
                            }
                        >
                            <FiArrowLeft />
                        </button>

                        <span className="sale-invoice-view-heading-icon">
                            <FiFileText />
                        </span>

                        <div>
                            <span>
                                SALE INVOICE
                            </span>

                            <h1>
                                {invoiceNumber}
                            </h1>

                            <p>
                                {getCustomerName(
                                    invoice
                                )}
                            </p>
                        </div>
                    </div>

                    <div className="sale-invoice-view-header-actions">
                        <button
                            type="button"
                            className="sale-invoice-view-action-secondary"
                            onClick={
                                loadInvoice
                            }
                        >
                            <FiRefreshCw />

                            Refresh
                        </button>

                        {documentStatus ===
                            "DRAFT" && (
                                <button
                                    type="button"
                                    className="sale-invoice-view-action-secondary"
                                    onClick={() =>
                                        history.push(
                                            `${SALE_INVOICE_ROUTE}/${invoiceId}/edit`
                                        )
                                    }
                                >
                                    <FiEdit3 />

                                    Edit
                                </button>
                            )}

                        {canReceivePayment(
                            invoice
                        ) && (
                                <button
                                    type="button"
                                    className="sale-invoice-view-action-payment"
                                    onClick={() =>
                                        setPaymentDialogOpen(
                                            true
                                        )
                                    }
                                >
                                    <FiCreditCard />

                                    Receive Payment
                                </button>
                            )}

                        <button
                            type="button"
                            className="sale-invoice-view-action-secondary"
                            onClick={() =>
                                history.push(
                                    `${SALE_INVOICE_ROUTE}/${invoiceId}/print`
                                )
                            }
                        >
                            <FiPrinter />

                            Print
                        </button>

                        <button
                            type="button"
                            className="sale-invoice-view-action-secondary"
                            disabled={
                                Boolean(
                                    actionLoading
                                )
                            }
                            onClick={
                                handleDuplicate
                            }
                        >
                            {actionLoading ===
                                "DUPLICATE" ? (
                                <FiLoader className="sale-invoice-view-spinner" />
                            ) : (
                                <FiCopy />
                            )}

                            Duplicate
                        </button>

                        {documentStatus ===
                            "CONFIRMED" && (
                                <button
                                    type="button"
                                    className="sale-invoice-view-action-danger"
                                    disabled={
                                        Boolean(
                                            actionLoading
                                        )
                                    }
                                    onClick={
                                        handleCancel
                                    }
                                >
                                    {actionLoading ===
                                        "CANCEL" ? (
                                        <FiLoader className="sale-invoice-view-spinner" />
                                    ) : (
                                        <FiXCircle />
                                    )}

                                    Cancel
                                </button>
                            )}
                    </div>
                </header>

                {pageError && (
                    <div className="sale-invoice-view-error">
                        <FiXCircle />

                        <span>
                            {pageError}
                        </span>
                    </div>
                )}

                {inventoryPostingStatus ===
                    "FAILED" && (
                        <div className="sale-invoice-view-stock-error-alert">
                            <div>
                                <FiAlertCircle />

                                <span>
                                    <strong>
                                        Stock posting failed
                                    </strong>

                                    <small>
                                        {retryStockError ||
                                            inventoryPostingError ||
                                            "The invoice was saved, but inventory could not be updated."}
                                    </small>
                                </span>
                            </div>

                            <div className="sale-invoice-view-stock-error-actions">
                                <button
                                    type="button"
                                    className="sale-invoice-view-stock-details-button"
                                    disabled={
                                        retryingStock
                                    }
                                    onClick={() =>
                                        setActiveTab(
                                            "STOCK"
                                        )
                                    }
                                >
                                    View Details
                                </button>

                                <button
                                    type="button"
                                    className="sale-invoice-view-stock-retry-button"
                                    disabled={
                                        retryingStock
                                    }
                                    onClick={
                                        handleRetryStockPosting
                                    }
                                >
                                    {retryingStock ? (
                                        <>
                                            <FiLoader className="sale-invoice-view-spinner" />

                                            Retrying...
                                        </>
                                    ) : (
                                        <>
                                            <FiRefreshCw />

                                            Retry Stock Posting
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                <section className="sale-invoice-view-overview">
                    <div className="sale-invoice-view-overview-main">
                        <div className="sale-invoice-view-status-row">
                            <span
                                className={`sale-invoice-view-badge sale-invoice-view-document-${documentStatus.toLowerCase()}`}
                            >
                                {formatLabel(
                                    documentStatus
                                )}
                            </span>

                            <span
                                className={`sale-invoice-view-badge sale-invoice-view-payment-${paymentStatus.toLowerCase()}`}
                            >
                                {formatLabel(
                                    paymentStatus
                                )}
                            </span>

                            <button
                                type="button"
                                className={`sale-invoice-view-stock-badge sale-invoice-view-stock-${inventoryPostingStatus.toLowerCase()}`}
                                onClick={() =>
                                    setActiveTab(
                                        "STOCK"
                                    )
                                }
                                title={
                                    inventoryPostingError ||
                                    getInventoryStatusLabel(
                                        inventoryPostingStatus
                                    )
                                }
                            >
                                <FiBox />

                                {getInventoryStatusLabel(
                                    inventoryPostingStatus
                                )}
                            </button>
                        </div>

                        <h2>
                            {getCustomerName(
                                invoice
                            )}
                        </h2>

                        <div className="sale-invoice-view-customer-contact">
                            <span>
                                <FiPhone />

                                {getCustomerMobile(
                                    invoice
                                )}
                            </span>

                            <span>
                                <FiMail />

                                {getCustomerEmail(
                                    invoice
                                )}
                            </span>
                        </div>
                    </div>

                    <div className="sale-invoice-view-financial-cards">
                        <div>
                            <span>
                                Invoice Total
                            </span>

                            <strong>
                                {displayCurrency(
                                    grandTotal
                                )}
                            </strong>
                        </div>

                        <div className="sale-invoice-view-financial-paid">
                            <span>
                                Paid
                            </span>

                            <strong>
                                {displayCurrency(
                                    paidAmount
                                )}
                            </strong>
                        </div>

                        <div className="sale-invoice-view-financial-balance">
                            <span>
                                Balance Due
                            </span>

                            <strong>
                                {displayCurrency(
                                    balanceAmount
                                )}
                            </strong>
                        </div>
                    </div>
                </section>

                {VIEW_TABS.map(
                    (tab) => (
                        <button
                            key={
                                tab.value
                            }
                            type="button"
                            className={
                                activeTab ===
                                    tab.value
                                    ? "sale-invoice-view-tab sale-invoice-view-tab-active"
                                    : "sale-invoice-view-tab"
                            }
                            onClick={() =>
                                setActiveTab(
                                    tab.value
                                )
                            }
                        >
                            {tab.label}

                            {tab.value ===
                                "PAYMENTS" && (
                                    <span>
                                        {paymentHistorySummary
                                            ?.paymentCount ||
                                            0}
                                    </span>
                                )}

                            {tab.value ===
                                "CREDIT_NOTES" && (
                                    <span>
                                        {
                                            invoiceCreditNotes.length
                                        }
                                    </span>
                                )}

                            {tab.value ===
                                "TIMELINE" && (
                                    <span>
                                        {
                                            timeline.length
                                        }
                                    </span>
                                )}
                        </button>
                    )
                )}

                {activeTab ===
                    "INVOICE" && (
                        <div className="sale-invoice-view-content">
                            <div className="sale-invoice-view-detail-grid">
                                <section className="sale-invoice-view-card">
                                    <h3>
                                        <FiFileText />

                                        Invoice Information
                                    </h3>

                                    <div className="sale-invoice-view-info-grid">
                                        <InfoField
                                            label="Invoice Number"
                                            value={
                                                invoiceNumber
                                            }
                                        />

                                        <InfoField
                                            label="Invoice Date"
                                            value={formatDate(
                                                invoice
                                                    ?.documentDate
                                            )}
                                        />

                                        <InfoField
                                            label="Due Date"
                                            value={formatDate(
                                                invoice
                                                    ?.dueDate
                                            )}
                                        />

                                        <InfoField
                                            label="Payment Terms"
                                            value={
                                                invoice
                                                    ?.paymentTerms ||
                                                    invoice
                                                        ?.paymentTermsDays
                                                    ? `${invoice.paymentTermsDays || invoice.paymentTerms} days`
                                                    : "-"
                                            }
                                        />

                                        <InfoField
                                            label="Reference"
                                            value={
                                                invoice
                                                    ?.referenceNumber
                                            }
                                        />

                                        <InfoField
                                            label="Sales Channel"
                                            value={formatLabel(
                                                invoice
                                                    ?.salesChannel
                                            )}
                                        />

                                        <InfoField
                                            label="Place of Supply"
                                            value={
                                                invoice
                                                    ?.placeOfSupply
                                            }
                                        />

                                        <InfoField
                                            label="Price Mode"
                                            value={formatLabel(
                                                invoice
                                                    ?.priceMode
                                            )}
                                        />
                                    </div>
                                </section>

                                <section className="sale-invoice-view-card sale-invoice-view-business-card">
                                    <h3>
                                        <FiUser />

                                        Business Details
                                    </h3>

                                    <div className="sale-invoice-view-business-header">
                                        <div className="sale-invoice-view-business-logo">
                                            {tenantBranding
                                                ?.logo ? (
                                                <img
                                                    src={
                                                        tenantBranding
                                                            .logo
                                                    }
                                                    alt={
                                                        tenantLegalName
                                                    }
                                                />
                                            ) : (
                                                <span>
                                                    {tenantLegalName
                                                        .charAt(
                                                            0
                                                        )}
                                                </span>
                                            )}
                                        </div>

                                        <div className="sale-invoice-view-business-main">
                                            <strong>
                                                {tenantLegalName}
                                            </strong>

                                            <span>
                                                {tenantAddressText}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="sale-invoice-view-info-grid sale-invoice-view-business-info-grid">
                                        <InfoField
                                            label="Mobile"
                                            value={
                                                tenantMobile
                                            }
                                        />

                                        <InfoField
                                            label="Email"
                                            value={
                                                tenantEmail
                                            }
                                        />

                                        <InfoField
                                            label="GSTIN"
                                            value={
                                                tenantGstin
                                            }
                                        />

                                        <InfoField
                                            label="PAN"
                                            value={
                                                tenantPan
                                            }
                                        />
                                    </div>
                                </section>

                                <section className="sale-invoice-view-card">
                                    <h3>
                                        <FiUser />

                                        Customer Details
                                    </h3>

                                    <div className="sale-invoice-view-info-grid">
                                        <InfoField
                                            label="Customer"
                                            value={getCustomerName(
                                                invoice
                                            )}
                                        />

                                        <InfoField
                                            label="Mobile"
                                            value={getCustomerMobile(
                                                invoice
                                            )}
                                        />

                                        <InfoField
                                            label="Email"
                                            value={getCustomerEmail(
                                                invoice
                                            )}
                                        />

                                        <InfoField
                                            label="GSTIN"
                                            value={getValue(
                                                invoice,
                                                [
                                                    "customerSnapshot.gstin",
                                                    "customer.gstin",
                                                ],
                                                "-"
                                            )}
                                        />

                                        <div className="sale-invoice-view-info-field sale-invoice-view-info-wide">
                                            <span>
                                                Billing Address
                                            </span>

                                            <strong>
                                                {getAddressText(
                                                    invoice
                                                        ?.billingAddressSnapshot ||
                                                    invoice
                                                        ?.billingAddress
                                                )}
                                            </strong>
                                        </div>

                                        <div className="sale-invoice-view-info-field sale-invoice-view-info-wide">
                                            <span>
                                                Shipping Address
                                            </span>

                                            <strong>
                                                {getAddressText(
                                                    invoice
                                                        ?.shippingAddressSnapshot ||
                                                    invoice
                                                        ?.shippingAddress
                                                )}
                                            </strong>
                                        </div>
                                    </div>
                                </section>
                            </div>

                            <section className="sale-invoice-view-card sale-invoice-view-items-card">
                                <h3>
                                    Invoice Items
                                </h3>

                                <div className="sale-invoice-view-table-wrap">
                                    <table className="sale-invoice-view-table">
                                        <thead>
                                            <tr>
                                                <th>
                                                    #
                                                </th>

                                                <th>
                                                    Item
                                                </th>

                                                <th>
                                                    HSN/SAC
                                                </th>

                                                <th>
                                                    Qty
                                                </th>

                                                <th>
                                                    Unit
                                                </th>

                                                <th className="sale-invoice-view-text-right">
                                                    Rate
                                                </th>

                                                <th className="sale-invoice-view-text-right">
                                                    Discount
                                                </th>

                                                <th className="sale-invoice-view-text-right">
                                                    Tax
                                                </th>

                                                <th className="sale-invoice-view-text-right">
                                                    Amount
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {items.map(
                                                (
                                                    item,
                                                    index
                                                ) => (
                                                    <tr
                                                        key={
                                                            item
                                                                ?._id ||
                                                            item
                                                                ?.temporaryId ||
                                                            index
                                                        }
                                                    >
                                                        <td>
                                                            {index +
                                                                1}
                                                        </td>

                                                        <td>
                                                            <strong>
                                                                {item
                                                                    ?.itemName ||
                                                                    item
                                                                        ?.productName ||
                                                                    "-"}
                                                            </strong>

                                                            {item
                                                                ?.description && (
                                                                    <small>
                                                                        {
                                                                            item.description
                                                                        }
                                                                    </small>
                                                                )}
                                                        </td>

                                                        <td>
                                                            {item
                                                                ?.hsnSacCode ||
                                                                item
                                                                    ?.hsnCode ||
                                                                "-"}
                                                        </td>

                                                        <td>
                                                            {toNumber(
                                                                item
                                                                    ?.quantity
                                                            )}
                                                        </td>

                                                        <td>
                                                            {formatLabel(
                                                                item
                                                                    ?.unit
                                                            )}
                                                        </td>

                                                        <td className="sale-invoice-view-text-right">
                                                            {displayCurrency(
                                                                item
                                                                    ?.rate ||
                                                                item
                                                                    ?.unitPrice
                                                            )}
                                                        </td>

                                                        <td className="sale-invoice-view-text-right">
                                                            {displayCurrency(
                                                                discountAmount
                                                            )}
                                                        </td>

                                                        <td className="sale-invoice-view-text-right">
                                                            {displayCurrency(
                                                                taxAmount
                                                            )}
                                                        </td>

                                                        <td className="sale-invoice-view-text-right sale-invoice-view-item-total">
                                                            {displayCurrency(
                                                                item
                                                                    ?.totalAmount ||
                                                                item
                                                                    ?.lineTotal
                                                            )}
                                                        </td>
                                                    </tr>
                                                )
                                            )}

                                            {!items.length && (
                                                <tr>
                                                    <td
                                                        colSpan="9"
                                                        className="sale-invoice-view-empty"
                                                    >
                                                        No invoice items available.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </section>

                            <div className="sale-invoice-view-bottom-grid">
                                <section className="sale-invoice-view-card">
                                    <h3>
                                        Notes & Terms
                                    </h3>

                                    <div className="sale-invoice-view-text-block">
                                        <span>
                                            Customer Notes
                                        </span>

                                        <p>
                                            {invoice
                                                ?.customerNotes ||
                                                invoice
                                                    ?.notes ||
                                                "No customer notes added."}
                                        </p>
                                    </div>

                                    <div className="sale-invoice-view-text-block">
                                        <span>
                                            Terms and Conditions
                                        </span>

                                        <p>
                                            {invoice
                                                ?.termsAndConditions ||
                                                "No terms and conditions added."}
                                        </p>
                                    </div>
                                </section>

                                <section className="sale-invoice-view-card sale-invoice-view-total-card">
                                    <h3>
                                        Invoice Summary
                                    </h3>

                                    <div className="sale-invoice-view-total-row">
                                        <span>
                                            Subtotal
                                        </span>

                                        <strong>
                                            {displayCurrency(
                                                subtotal
                                            )}
                                        </strong>
                                    </div>

                                    <div className="sale-invoice-view-total-row sale-invoice-view-total-discount">
                                        <span>
                                            Discount
                                        </span>

                                        <strong>
                                            -
                                            {displayCurrency(
                                                invoice
                                                    ?.totals
                                                    ?.discountAmount
                                            )}
                                        </strong>
                                    </div>

                                    <div className="sale-invoice-view-total-row">
                                        <span>
                                            Tax
                                        </span>

                                        <strong>
                                            {displayCurrency(
                                                invoice
                                                    ?.totals
                                                    ?.taxAmount
                                            )}
                                        </strong>
                                    </div>

                                    <div className="sale-invoice-view-total-row">
                                        <span>
                                            Additional Charges
                                        </span>

                                        <strong>
                                            {displayCurrency(
                                                additionalChargesAmount
                                            )}
                                        </strong>
                                    </div>

                                    <div className="sale-invoice-view-total-row">
                                        <span>
                                            Round Off
                                        </span>

                                        <strong>
                                            {displayCurrency(
                                                roundOffAmount
                                            )}
                                        </strong>
                                    </div>

                                    <div className="sale-invoice-view-total-row sale-invoice-view-total-grand">
                                        <span>
                                            Grand Total
                                        </span>

                                        <strong>
                                            {displayCurrency(
                                                grandTotal
                                            )}
                                        </strong>
                                    </div>

                                    <div className="sale-invoice-view-total-row sale-invoice-view-total-paid">
                                        <span>
                                            Paid
                                        </span>

                                        <strong>
                                            {displayCurrency(
                                                paidAmount
                                            )}
                                        </strong>
                                    </div>

                                    <div className="sale-invoice-view-total-row sale-invoice-view-total-balance">
                                        <span>
                                            Balance Due
                                        </span>

                                        <strong>
                                            {displayCurrency(
                                                balanceAmount
                                            )}
                                        </strong>
                                    </div>
                                </section>
                            </div>
                        </div>
                    )}
                {activeTab ===
                    "PAYMENTS" && (
                        <SaleInvoicePaymentsPanel
                            invoice={
                                invoice
                            }
                            onPaymentClick={
                                handleOpenPaymentDetails
                            }
                            onViewPayment={
                                handleOpenPaymentDetails
                            }
                            onInvoiceRefresh={async () => {
                                try {
                                    setPageError(
                                        ""
                                    );

                                    await refreshInvoicePaymentData();
                                } catch (
                                error
                                ) {
                                    setPageError(
                                        getErrorMessage(
                                            error,
                                            "Unable to refresh invoice payments."
                                        )
                                    );
                                }
                            }}
                        />
                    )}
                {activeTab ===
                    "CREDIT_NOTES" && (
                        <CreditNotesPanel
                            invoice={
                                invoice
                            }
                            onInvoiceRefresh={async () => {
                                try {
                                    setPageError(
                                        ""
                                    );

                                    await Promise.all([
                                        dispatch(
                                            fetchSaleInvoiceById(
                                                invoiceId
                                            )
                                        ).unwrap(),

                                        dispatch(
                                            fetchSaleInvoiceHistory({
                                                documentId:
                                                    invoiceId,

                                                invoiceId,

                                                page:
                                                    1,

                                                limit:
                                                    100,
                                            })
                                        ).unwrap(),
                                    ]);
                                } catch (
                                error
                                ) {
                                    setPageError(
                                        getErrorMessage(
                                            error,
                                            "Credit note was updated, but the invoice could not be refreshed."
                                        )
                                    );
                                }
                            }}
                        />
                    )}
                {activeTab ===
                    "STOCK" && (
                        <SaleInvoiceStockMovements
                            invoiceId={
                                invoiceId
                            }
                            inventoryPostingStatus={
                                invoice
                                    ?.inventoryPostingStatus ||
                                "PENDING"
                            }
                        />
                    )}

                {activeTab ===
                    "TIMELINE" && (
                        <section className="sale-invoice-view-card sale-invoice-view-tab-panel">
                            <div className="sale-invoice-view-panel-heading">
                                <div>
                                    <h3>
                                        <FiClock />

                                        Invoice Timeline
                                    </h3>

                                    <p>
                                        Complete activity history for this invoice.
                                    </p>
                                </div>
                            </div>

                            {historyLoading ? (
                                <div className="sale-invoice-view-timeline-loading">
                                    <FiLoader className="sale-invoice-view-spinner" />

                                    Loading invoice history...
                                </div>
                            ) : (
                                <div className="sale-invoice-view-timeline">
                                    {timeline.map(
                                        (
                                            entry,
                                            index
                                        ) => (
                                            <div
                                                key={
                                                    entry
                                                        ?._id ||
                                                    index
                                                }
                                                className="sale-invoice-view-timeline-entry"
                                            >
                                                <span className="sale-invoice-view-timeline-icon">
                                                    {String(
                                                        entry
                                                            ?.action ||
                                                        entry
                                                            ?.eventType ||
                                                        ""
                                                    ).includes(
                                                        "CANCEL"
                                                    ) ? (
                                                        <FiXCircle />
                                                    ) : String(
                                                        entry
                                                            ?.action ||
                                                        entry
                                                            ?.eventType ||
                                                        ""
                                                    ).includes(
                                                        "PAYMENT"
                                                    ) ? (
                                                        <FiCreditCard />
                                                    ) : (
                                                        <FiCheckCircle />
                                                    )}
                                                </span>

                                                <div>
                                                    <strong>
                                                        {formatLabel(
                                                            entry
                                                                ?.action ||
                                                            entry
                                                                ?.eventType ||
                                                            entry
                                                                ?.type ||
                                                            "ACTIVITY"
                                                        )}
                                                    </strong>

                                                    <p>
                                                        {entry
                                                            ?.description ||
                                                            entry
                                                                ?.message ||
                                                            entry
                                                                ?.notes ||
                                                            "Invoice activity recorded."}
                                                    </p>

                                                    <small>
                                                        <FiCalendar />

                                                        {formatDate(
                                                            entry
                                                                ?.createdAt ||
                                                            entry
                                                                ?.actionDate,
                                                            true
                                                        )}
                                                    </small>
                                                </div>
                                            </div>
                                        )
                                    )}

                                    {!timeline.length && (
                                        <div className="sale-invoice-view-empty-state">
                                            <FiClock />

                                            <strong>
                                                No timeline available
                                            </strong>

                                            <span>
                                                Invoice activity will appear here.
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    )}
                {activeTab ===
                    "LEDGER" && (
                        <CustomerLedgerStatement
                            invoice={
                                invoice
                            }
                            onInvoiceClick={(
                                entry
                            ) => {
                                const documentId =
                                    entry
                                        ?.document
                                        ?._id ||
                                    entry
                                        ?.sourceId;

                                if (
                                    documentId
                                ) {
                                    history.push(
                                        `${SALE_INVOICE_ROUTE}/${documentId}`
                                    );
                                }
                            }}
                            onPaymentClick={
                                handleOpenPaymentDetails
                            }
                        />
                    )}
                {paymentDialogOpen && (
                    <SaleInvoicePaymentDialog
                        open={
                            paymentDialogOpen
                        }
                        invoice={
                            invoice
                        }
                        onClose={() =>
                            setPaymentDialogOpen(
                                false
                            )
                        }
                        onSuccess={
                            handlePaymentSuccess
                        }
                    />
                )}
                <SaleInvoicePaymentDetailsDialog
                    open={
                        ledgerPaymentDialogOpen
                    }
                    payment={
                        ledgerSelectedPayment
                    }
                    invoice={
                        invoice
                    }
                    reversing={
                        paymentReversalLoading
                    }
                    onClose={() => {
                        if (
                            paymentReversalLoading
                        ) {
                            return;
                        }

                        setLedgerPaymentDialogOpen(
                            false
                        );

                        setLedgerSelectedPayment(
                            null
                        );
                    }}
                    onReverse={
                        handleReversePayment
                    }
                    onAllocatePayment={(
                        selectedPayment
                    ) => {
                        const availableAmount =
                            roundMoney(
                                selectedPayment
                                    ?.unallocatedAmount ??
                                selectedPayment
                                    ?.availableAmount ??
                                selectedPayment
                                    ?.payment
                                    ?.unallocatedAmount ??
                                Math.max(
                                    toNumber(
                                        selectedPayment
                                            ?.amount ??
                                        selectedPayment
                                            ?.paymentAmount ??
                                        selectedPayment
                                            ?.payment
                                            ?.amount
                                    ) -
                                    toNumber(
                                        selectedPayment
                                            ?.allocatedAmount ??
                                        selectedPayment
                                            ?.payment
                                            ?.allocatedAmount
                                    ),
                                    0
                                )
                            );

                        if (
                            availableAmount <=
                            0
                        ) {
                            setPageError(
                                "This payment is fully allocated. No amount is available for further allocation."
                            );

                            return;
                        }

                        setLedgerPaymentDialogOpen(
                            false
                        );

                        setLedgerSelectedPayment(
                            null
                        );

                        handleOpenAllocatePayment({
                            ...selectedPayment,

                            unallocatedAmount:
                                availableAmount,

                            availableAmount,
                        });
                    }}
                />
                <AllocatePaymentDialog
                    open={
                        allocatePaymentDialogOpen
                    }
                    payment={
                        paymentForAllocation
                    }
                    invoice={
                        invoice
                    }
                    onClose={
                        handleCloseAllocatePayment
                    }
                    onAllocated={async () => {
                        try {
                            handleCloseAllocatePayment();

                            setPageError(
                                ""
                            );

                            await refreshInvoicePaymentData();

                            setActiveTab(
                                "PAYMENTS"
                            );
                        } catch (
                        error
                        ) {
                            setPageError(
                                getErrorMessage(
                                    error,
                                    "Payment was allocated, but refreshed data could not be loaded."
                                )
                            );
                        }
                    }}
                />
            </div>

        );
    };

export {
    formatCurrency,
    formatDate,
    formatLabel,
    getInvoiceNumber,
    getDocumentStatus,
    getPaymentStatus,
    getGrandTotal,
    getPaidAmount,
    getBalanceAmount,
    canReceivePayment,
};

export default SaleInvoiceViewPage;